// 地図ビュー（deck.gl キャンバス）。
//
// 役割: 全画面の DeckGL を描画し、OSM ベース + データレイヤーを表示する。
//       フィーチャのホバー/クリックで属性ツールチップを出す。描画モード時は
//       EditableGeoJsonLayer を最前面に重ね、点/線/面を描けるようにする。
// 関係: 描画レイヤーは Layers/index.js の renderLayers() に委譲。viewState は App が保持し、
//       操作（パン/ズーム）と自動フィットの双方で更新する。描画の状態（drawMode/drawnFeatures）と
//       更新コールバック（onDrawEdit）は App から渡る。
//
// 流用元: reference/deckgl-duckdb-template/src/App.jsx の DeckGL 部分を分離。
import DeckGL from '@deck.gl/react'
import {
  EditableGeoJsonLayer,
  DrawPointMode,
  DrawLineStringMode,
  DrawPolygonMode,
  ViewMode,
} from '@deck.gl-community/editable-layers'
import { renderLayers } from '../Layers'
import { MAP_CANVAS_ID } from '../utils/capture-map'

// 描画モード名 → editable-layers のモードクラス。
const DRAW_MODES = {
  point: DrawPointMode,
  line: DrawLineStringMode,
  polygon: DrawPolygonMode,
}

// ピックされたフィーチャの属性をツールチップ文字列にする（先頭 8 列まで）。
function getTooltip({ object }) {
  if (!object) return null
  const props = object.properties ?? {}
  const entries = Object.entries(props).slice(0, 8)
  if (entries.length === 0) return null
  return { text: entries.map(([k, v]) => `${k}: ${v}`).join('\n') }
}

function MapView({
  viewState,
  onViewStateChange,
  layers,
  drawMode = 'none',
  drawnFeatures,
  onDrawEdit,
  overlay,
}) {
  const baseLayers = renderLayers(layers)

  // 描画モード時のみ、最前面に編集可能レイヤーを重ねる。
  const drawing = drawMode !== 'none'
  if (drawing) {
    baseLayers.push(
      new EditableGeoJsonLayer({
        id: 'draw-layer',
        data: drawnFeatures,
        mode: DRAW_MODES[drawMode] ?? ViewMode,
        selectedFeatureIndexes: [],
        onEdit: ({ updatedData }) => onDrawEdit?.(updatedData),
      }),
    )
  }

  return (
    <div className={`map-view${drawing ? ' drawing' : ''}`}>
      <DeckGL
        // canvas の id は音声機能のスクリーンショット取得（utils/capture-map）が参照する。
        // @deck.gl/react の既定値と同じ値だが、暗黙の依存にしないため明示する。
        id={MAP_CANVAS_ID}
        viewState={viewState}
        controller={true}
        onViewStateChange={({ viewState: vs }) => onViewStateChange(vs)}
        layers={baseLayers}
        getTooltip={getTooltip}
      />
      {/* 地図上に浮かべるコントロール（描画ツールバー等）。絶対配置で重ねる。 */}
      {overlay && <div className="map-overlay map-overlay-tl">{overlay}</div>}
      <div className="attribution">
        <a href="http://www.openstreetmap.org/about/" target="_blank" rel="noopener noreferrer">
          © OpenStreetMap
        </a>
      </div>
    </div>
  )
}

export default MapView
